import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, Crown, ExternalLink, Instagram, Globe, Calendar } from 'lucide-react';
import api from '../services/api';

const DeepWaveCrew = ({ limit = null, showTitle = true, showViewAll = true, expanded = false }) => {
    const [crewMembers, setCrewMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchCrewMembers();
    }, [limit]);

    const fetchCrewMembers = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await api.get(`/profile/crew/members${limit ? `?limit=${limit}` : ''}`);
            
            if (response.data.success) {
                setCrewMembers(response.data.data);
            } else {
                setError('Failed to load crew members');
            }
        } catch (err) {
            console.error('Error fetching crew members:', err);
            setError('Unable to load crew members');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="glass-card p-4 sm:p-6">
                {showTitle && (
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                            <Crown className="w-5 h-5 text-yellow-400" />
                            The Deep Wave Crew
                        </h3>
                    </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                    {[...Array(4)].map((_, index) => (
                        <div key={index} className="glass-card p-4 animate-pulse">
                            <div className="w-16 h-16 bg-gray-700 rounded-full mx-auto mb-3"></div>
                            <div className="h-4 bg-gray-700 rounded mb-2"></div>
                            <div className="h-3 bg-gray-700 rounded w-3/4 mx-auto"></div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="glass-card p-4 sm:p-6">
                {showTitle && (
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                            <Crown className="w-5 h-5 text-yellow-400" />
                            The Deep Wave Crew
                        </h3>
                    </div>
                )}
                <div className="text-center py-8">
                    <Users className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400">{error}</p>
                </div>
            </div>
        );
    }

    if (!crewMembers || crewMembers.length === 0) {
        return (
            <div className="glass-card p-4 sm:p-6">
                {showTitle && (
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                            <Crown className="w-5 h-5 text-yellow-400" />
                            The Deep Wave Crew
                        </h3>
                    </div>
                )}
                <div className="text-center py-8">
                    <Users className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400">No crew members found</p>
                </div>
            </div>
        );
    }

    return (
        <div className="glass-card p-4 sm:p-6">
            {showTitle && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3 sm:gap-0">
                    <h3 className="text-xl font-bold text-white flex items-center gap-2">
                        <Crown className="w-5 h-5 text-yellow-400" />
                        The Deep Wave Crew
                    </h3>
                    {showViewAll && (
                        <Link
                            to="/community"
                            className="text-purple-400 hover:text-purple-300 text-sm flex items-center gap-1 self-start sm:self-auto"
                        >
                            View All <ExternalLink className="w-3 h-3" />
                        </Link>
                    )}
                </div>
            )}

            <div className="flex flex-wrap gap-3 sm:gap-4 justify-center">
                {crewMembers.map((member) => (
                    <div
                        key={member.id}
                        className="glass-card p-3 sm:p-4 hover:transform hover:scale-105 transition-all duration-300 group w-full sm:flex-[0_0_calc(25%-12px)]"
                    >
                        <div className="text-center">
                            {/* Profile Picture */}
                            <div className="relative mb-3">
                                {member.profile_picture_url ? (
                                    <img
                                        src={member.profile_picture_url}
                                        alt={member.display_name || member.username}
                                        width="120"
                                        height="120"
                                        className="rounded-full mx-auto object-cover border-2 border-yellow-400/30"
                                        style={{ maxWidth: '75%', margin: '0 auto' }}
                                        onLoad={(e) => {
                                            e.target.style.setProperty('max-width', '75%', 'important');
                                            e.target.style.setProperty('margin', '0 auto', 'important');
                                        }}
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.nextSibling.style.display = 'flex';
                                        }}
                                    />
                                ) : null}
                                <div
                                    className={`w-18 h-18 sm:w-24 sm:h-24 rounded-full mx-auto flex items-center justify-center bg-gradient-to-r from-yellow-500 to-orange-500 border-2 border-yellow-400/30 ${
                                        member.profile_picture_url ? 'hidden' : 'flex'
                                    }`}
                                    style={{ maxWidth: '75%' }}
                                >
                                    <span className="text-3xl sm:text-4xl leading-none">🌊</span>
                                </div>
                                {/* Crown badge for crew members */}
                                <div className="absolute -top-1 -right-1 w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center">
                                    <Crown className="w-3 h-3 text-yellow-900" />
                                </div>
                            </div>

                            {/* Name and Role */}
                            <h4 className="text-white font-bold text-xs sm:text-sm mb-1 truncate">
                                {member.display_name || member.username || 'Crew Member'}
                            </h4>
                            
                            <p className="text-yellow-400 text-xs font-semibold mb-2">
                                {member.crew_role || 'Crew Member'}
                            </p>

                            {/* Bio if available */}
                            {member.crew_bio && (
                                <p className="text-gray-400 text-xs mb-3 line-clamp-2 hidden sm:block">
                                    {member.crew_bio}
                                </p>
                            )}

                            {/* Join Date */}
                            <div className="flex items-center justify-center text-gray-500 text-xs">
                                <Calendar className="w-3 h-3 mr-1" />
                                <span>
                                    Joined {new Date(member.crew_joined_date).toLocaleDateString('en-US', {
                                        month: 'short',
                                        year: 'numeric'
                                    })}
                                </span>
                            </div>

                            {/* View Profile Button */}
                            <Link
                                to={`/profile/${member.username}`}
                                className="mt-2 sm:mt-3 block w-full py-1.5 sm:py-2 px-2 sm:px-3 bg-yellow-600/20 hover:bg-yellow-600/30 text-yellow-400 text-xs rounded-lg transition-colors duration-200 border border-yellow-600/30 text-center"
                            >
                                View Profile
                            </Link>
                        </div>
                    </div>
                ))}
            </div>

            {/* Load More hint if there are more members */}
            {limit && crewMembers.length >= limit && showViewAll && (
                <div className="text-center mt-6">
                    <p className="text-gray-400 text-sm">
                        Showing {crewMembers.length} crew members
                    </p>
                </div>
            )}
        </div>
    );
};

export default DeepWaveCrew;